// src/store/index.ts
import { BehaviorSubject } from 'rxjs'
import { map } from 'rxjs/operators'

// Define the state interfaces
export interface ChatState {
    chats: Record<string, Chat>
    activeChatId: string | null
    isLoading: boolean
    error: string | null
    characters: Record<string, Character>
    activeCharacter: string | null
}

export interface Character {
    id: string
    name: string
    type: 'stateful' | 'stateless'
    state?: {
        currentState: string
        progress: number
    }
}

export interface Chat {
    id: string
    name: string
    personalityId?: string
    lastMessage: string
    timestamp: number
    messages: Message[]
}

export interface Message {
    id: string
    text: string
    sender: 'user' | 'bot'
    timestamp: number
    status?: 'sent' | 'received'
}

// Initial state
const initialState: ChatState = {
    chats: {},
    activeChatId: null,
    isLoading: false,
    error: null,
    characters: {},
    activeCharacter: null,
}

// Create the main state subject
const state = new BehaviorSubject<ChatState>(initialState)

// Helper to generate unique IDs
const generateId = (): string => {
    return Math.random().toString(36).substring(2, 11)
}

// Store actions
export const chatStore = {
    setCharacters: (characters: Character[]) => {
        const currentState = state.getValue()
        const charactersMap = characters.reduce(
            (acc, char) => {
                acc[char.id] = char
                return acc
            },
            {} as Record<string, Character>,
        )

        state.next({
            ...currentState,
            characters: charactersMap,
        })
    },

    updateCharacterState: (
        characterId: string,
        newState: { currentState: string; progress: number },
    ) => {
        const currentState = state.getValue()
        const character = currentState.characters[characterId]

        if (character) {
            state.next({
                ...currentState,
                characters: {
                    ...currentState.characters,
                    [characterId]: {
                        ...character,
                        state: newState,
                    },
                },
            })
        }
    },

    setActiveCharacter: (characterId: string | null) => {
        const currentState = state.getValue()
        state.next({
            ...currentState,
            activeCharacter: characterId,
        })
    },

    // Get the current state
    getState: () => state.getValue(),

    // Subscribe to the entire state
    subscribe: (callback: (state: ChatState) => void) => {
        return state.subscribe(callback)
    },

    // Subscribe to a specific slice of state
    select: <T>(selector: (state: ChatState) => T) => {
        return state.pipe(map(selector))
    },

    // Get active chat
    getActiveChat: () => {
        const currentState = state.getValue()
        const activeChatId = currentState.activeChatId
        return activeChatId ? currentState.chats[activeChatId] : null
    },

    // Set active chat
    setActiveChat: (chatId: string) => {
        const currentState = state.getValue()
        if (!currentState.chats[chatId]) {
            console.error(`Chat with ID ${chatId} not found`)
            return
        }

        state.next({
            ...currentState,
            activeChatId: chatId,
        })
    },

    // Initialize chat if it doesn't exist
    initializeChat: (
        chatId: string,
        name: string,
        lastMessage: string = '',
        personalityId?: string,
    ) => {
        const currentState = state.getValue()

        if (!currentState.chats[chatId]) {
            state.next({
                ...currentState,
                chats: {
                    ...currentState.chats,
                    [chatId]: {
                        id: chatId,
                        name,
                        personalityId,
                        lastMessage,
                        timestamp: Date.now(),
                        messages: [],
                    },
                },
                activeChatId: currentState.activeChatId || chatId,
            })
        }
    },

    // Add a user message to a specific chat
    addUserMessage: (text: string, chatId?: string, isHistorical: boolean = false) => {
        const currentState = state.getValue()
        const targetChatId = chatId || currentState.activeChatId

        if (!targetChatId || !currentState.chats[targetChatId]) {
            console.error('No active chat or chat not found')
            return
        }

        const newMessage: Message = {
            id: generateId(),
            text,
            sender: 'user',
            timestamp: isHistorical ? Date.now() - 10000 : Date.now(),
            status: isHistorical ? 'received' : 'sent',
        }

        const targetChat = currentState.chats[targetChatId]
        const updatedChat = {
            ...targetChat,
            messages: [...targetChat.messages, newMessage],
            lastMessage: text,
            timestamp: newMessage.timestamp,
        }

        state.next({
            ...currentState,
            chats: {
                ...currentState.chats,
                [targetChatId]: updatedChat,
            },
            isLoading: !isHistorical,
        })
    },

    // Add a bot message to a specific chat
    addBotMessage: (text: string, chatId?: string, isHistorical: boolean = false) => {
        const currentState = state.getValue()
        const targetChatId = chatId || currentState.activeChatId

        if (!targetChatId || !currentState.chats[targetChatId]) {
            console.error('No active chat or chat not found')
            return
        }

        const newMessage: Message = {
            id: generateId(),
            text,
            sender: 'bot',
            timestamp: isHistorical ? Date.now() - 9000 : Date.now(),
            status: 'received',
        }

        const targetChat = currentState.chats[targetChatId]
        const updatedChat = {
            ...targetChat,
            messages: [...targetChat.messages, newMessage],
            lastMessage: text,
            timestamp: newMessage.timestamp,
        }

        state.next({
            ...currentState,
            chats: {
                ...currentState.chats,
                [targetChatId]: updatedChat,
            },
            isLoading: false,
        })
    },

    updateBotMessage: (text: string, chatId?: string) => {
        const currentState = state.getValue()
        const targetChatId = chatId || currentState.activeChatId

        if (!targetChatId || !currentState.chats[targetChatId]) {
            console.error('No active chat or chat not found')
            return
        }

        const targetChat = currentState.chats[targetChatId]

        // Find the last bot message and update it
        const messages = [...targetChat.messages]
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].sender === 'bot') {
                messages[i] = { ...messages[i], text }
                break
            }
        }

        state.next({
            ...currentState,
            chats: {
                ...currentState.chats,
                [targetChatId]: {
                    ...targetChat,
                    messages,
                    lastMessage: text,
                    timestamp: Date.now(),
                },
            },
        })
    },

    // Clear messages from a specific chat
    clearMessages: (chatId?: string) => {
        const currentState = state.getValue()
        const targetChatId = chatId || currentState.activeChatId

        if (!targetChatId || !currentState.chats[targetChatId]) {
            console.error('No active chat or chat not found')
            return
        }

        const targetChat = currentState.chats[targetChatId]
        const updatedChat = {
            ...targetChat,
            messages: [],
            lastMessage: '',
        }

        state.next({
            ...currentState,
            chats: {
                ...currentState.chats,
                [targetChatId]: updatedChat,
            },
        })
    },

    // Set error
    setError: (error: string | null) => {
        const currentState = state.getValue()
        state.next({
            ...currentState,
            error,
        })
    },
}

const storeDebugger = {
    // State inspection
    getState: () => state.getValue(),
    getActiveChat: () => chatStore.getActiveChat(),
    getCharacters: () => state.getValue().characters,
    getActiveCharacter: () => state.getValue().activeCharacter,

    // Chat operations
    createChat: (name: string) => {
        const chatId = generateId()
        chatStore.initializeChat(chatId, name)
        return `Created chat: ${chatId}`
    },

    sendMessage: (text: string, sender: 'user' | 'bot' = 'user') => {
        if (sender === 'user') {
            chatStore.addUserMessage(text)
        } else {
            chatStore.addBotMessage(text)
        }
        return `Sent ${sender} message: ${text}`
    },

    // Character operations
    addCharacter: (character: Character) => {
        chatStore.setCharacters([...Object.values(state.getValue().characters), character])
        return `Added character: ${character.id}`
    },

    updateCharacterProgress: (characterId: string, progress: number) => {
        const character = state.getValue().characters[characterId]
        if (character?.state) {
            chatStore.updateCharacterState(characterId, {
                ...character.state,
                progress,
            })
            return `Updated ${characterId} progress to ${progress}%`
        }
        return `Character ${characterId} not found or has no state`
    },

    // Utility functions
    clear: () => {
        const currentState = state.getValue()
        Object.keys(currentState.chats).forEach(chatId => {
            chatStore.clearMessages(chatId)
        })
        return 'Cleared all chat messages'
    },

    reset: () => {
        state.next(initialState)
        return 'Reset store to initial state'
    },

    // Store statistics
    stats: () => ({
        chats: Object.keys(state.getValue().chats).length,
        characters: Object.keys(state.getValue().characters).length,
        activeChat: state.getValue().activeChatId,
        activeCharacter: state.getValue().activeCharacter,
        totalMessages: Object.values(state.getValue().chats).reduce(
            (acc, chat) => acc + chat.messages.length,
            0,
        ),
    }),

    // Help command
    help: () => ({
        'store.getState()': 'Get complete store state',
        'store.getActiveChat()': 'Get current active chat',
        'store.getCharacters()': 'Get all characters',
        'store.getActiveCharacter()': 'Get current active character',
        'store.createChat(name)': 'Create a new chat',
        'store.sendMessage(text, sender?)': 'Send a message in active chat',
        'store.addCharacter(character)': 'Add a new character',
        'store.updateCharacterProgress(id, progress)': 'Update character progress',
        'store.clear()': 'Clear all chat messages',
        'store.reset()': 'Reset store to initial state',
        'store.stats()': 'Get store statistics',
    }),
}

// Make the debugger available in the console
declare global {
    interface Window {
        store: typeof storeDebugger
    }
}

window.store = storeDebugger
